import AuthorizationService from '@auth/services/authorization.service';
import { get, set } from 'lodash';
import moment from 'moment';
import router from '@app/router/index.APP_TARGET';
import { Route } from 'vue-router';
import store from '@app/store';
import ClientRouterService from '@core/services/client-router.service';
import { format } from 'date-fns';

export default class BPCModelFactoryService {
  $authorizationService: AuthorizationService;
  $clientRouterService: ClientRouterService;

  constructor() {
    this.$authorizationService = new AuthorizationService();
    this.$clientRouterService = new ClientRouterService();
  }

  currentRoute(): Route {
    return router?.currentRoute || null;
  }

  getInitialFormModel(
    fields: object,
    entityId: string | number = null,
    model: any | null = null,
    extraData: any = null,
  ): AnyEntity {
    const modelForm = {};

    // Function to compute default value based on 'computedString'
    const computeDefaultValue = field => {
      switch (field.computedString) {
        case 'today':
          return format(new Date(), 'yyyy-MM-dd');
        case 'now':
          return format(new Date(), 'HH:mm');
        case 'settings-invoice-due-date': {
          const nbDaysDueDate =
            store.getters['centerSettings/centerSettings'].invoice_nbdays_for_due_date;
          return (
            moment()
              .add(nbDaysDueDate, 'day')
              .format('YYYY-MM-DD') ||
            moment()
              .add(1, 'M')
              .format('YYYY-MM-DD')
          );
        }
        case 'next-month':
          return moment()
            .add(1, 'M')
            .format('YYYY-MM-DD');
        case 'my-profile': {
          const tenant = store.getters['tenant/selectedTenant'];
          return store.getters['auth/profile']({ tenantId: tenant.id })?.id;
        }
        case 'my-contact':
          return this.$authorizationService.getContactId();
        case 'my-contact-array':
          return [this.$authorizationService.getContactId()];
        case 'logged-coach-array': {
          const tenant_type = store.getters['centerSettings/centerSettings'].center_type;
          return this.$authorizationService.hasRole('trainer_coach') && tenant_type === 'center'
            ? [this.$authorizationService.getContactId()]
            : [];
        }
        case 'contact-form-id':
          return extraData.filters.find(filter => filter.filterField === 'contact_id')?.value;
        case 'show-contact':
          return this.currentRoute().params['contact_id'];
        case 'show-contact-array':
          return [this.currentRoute().params['contact_id']];
        case 'pos-default-contact':
          return store.getters['centerSettings/posDefaultContactId'];
        case 'settings-email':
          return store.getters['centerSettings/centerSettings'].email1;
        case 'pos-current-box-id':
          return store.getters['pos/currentPosBoxId'];
        case 'settings-admin-send-enrollment-confirmation-email':
          return store.getters['centerSettings/centerSettings']
            ?.admin_send_enrollment_confirmation_email;
        case 'settings-admin-send-enrollment-cancellation-email':
          return store.getters['centerSettings/centerSettings']
            ?.admin_send_enrollment_cancellation_email;
        case 'settings-admin-send-subscription-confirmation-email':
          return store.getters['centerSettings/centerSettings']
            ?.admin_send_subscription_confirmation_email;
        case 'settings-admin-send-subscription-cancellation-email':
          return store.getters['centerSettings/centerSettings']
            ?.admin_send_subscription_cancellation_email;
        case 'settings-admin-send-booking-confirmation-email':
          return store.getters['centerSettings/centerSettings']
            ?.admin_send_booking_confirmation_email;
        case 'settings-admin-send-booking-cancellation-email':
          return store.getters['centerSettings/centerSettings']
            ?.admin_send_booking_cancellation_email;
        case 'settings-admin-send-attendee-created-email':
          return store.getters['centerSettings/centerSettings']?.admin_send_attendee_created_email;
        case 'settings-admin-send-attendee-cancelled-email':
          return store.getters['centerSettings/centerSettings']
            ?.admin_send_attendee_cancelled_email;
        case 'settings-admin-send-session-updated-email':
          return store.getters['centerSettings/centerSettings']?.admin_send_session_updated_email;
        case 'default-subsidizing-contact':
          return store.getters['centerSettings/defaultSubsidizingContact'];
        case 'default-price-category':
          return store.getters['centerSettings/defaultPriceCategory']?.id;
        case 'new-contact-form-url':
          return this.$clientRouterService.getFullClientUrl('new-contact');
        // NOTE: i think is not in use anymore
        // case 'translate-default-value':
        //   return String(i18n.t(def[2]));
        case 'settings-default-value-for-free-contact-bookings':
          return store.getters['centerSettings/centerSettings']
            ?.default_value_for_free_contact_bookings;
        case 'settings-default-tenant-language':
          return store.getters['centerSettings/centerSettings']?.default_language;
        default:
          return null;
      }
    };

    // Function to compute append icon
    const computeAppendIcon = (field, modelForm) => {
      if (field.computedAppendIcon) {
        switch (field.computedAppendIcon) {
          case 'discount-type': {
            const discountType = modelForm['discount_type'] || modelForm['type'];
            switch (discountType) {
              case 'percentage':
              case 'percent':
                field.appendIcon = 'percent';
                break;
              case 'fixed':
              case 'money':
                field.appendIcon = 'euro-sign';
                break;
              default:
                field.appendIcon = null;
            }
            break;
          }
          default:
            field.appendIcon = null;
        }
      }
    };

    Object.values(fields).forEach(field => {
      if (field.defaultValue && field.defaultValue.toString().indexOf('computed') > -1) {
        // First time computation
        field.computedString = field.defaultValue.split(':')[1];
        field.defaultValue = computeDefaultValue(field);
        field.hasBeenComputed = true;
      } else if (field.computedString && field.hasBeenComputed) {
        field.defaultValue = computeDefaultValue(field);
      }

      const fieldValue = JSON.parse(
        JSON.stringify(
          get(
            model,
            field.fieldReadPath,
            !field.completeWithExtraData
              ? get(field, 'defaultValue', null)
              : get(extraData, field.completeWithExtraData, null),
          ),
        ),
      );

      computeAppendIcon(field, modelForm);

      set(modelForm, field.fieldPath, this.parseFieldValue(fieldValue, field));
    });

    Object.values(fields).forEach(field => {
      this.setFilters(field, modelForm);
    });

    if (entityId) {
      modelForm['id'] = entityId;
    }
    return modelForm;
  }

  private parseFieldValue(fieldValue, field) {
    if (field.parseAsDateFormat) {
      return this.parseAsDateFormat(fieldValue, field);
    }

    return fieldValue;
  }

  private parseAsDateFormat(fieldValue, field) {
    if (fieldValue.includes('now+')) {
      const toAdd = Number(fieldValue.replace('now+', ''));
      return moment()
        .add(toAdd, 'days')
        .format(field.parseAsDateFormat);
    } else if (fieldValue.includes('now-')) {
      const toSubtract = Number(fieldValue.replace('now-', ''));
      return moment()
        .subtract(toSubtract, 'days')
        .format(field.parseAsDateFormat);
    }
    return moment().format(field.parseAsDateFormat);
  }

  public setFilters(field: any, model: AnyEntity) {
    if (field.filters) {
      field.filters.forEach(filter => {
        if (filter['modelField']) {
          if (filter['modelField'].includes('.')) {
            const [relation, fieldName] = filter['modelField'].split('.');
            set(filter, 'value', model[relation]?.[fieldName]);
          } else {
            set(filter, 'value', model[filter['modelField']]);
          }
        }
      });
    }
  }
}
